import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { ChevronRight, Menu as MenuIcon, Search, Sparkles } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { usePermissions } from '../../hooks/usePermissions';
import { isAiChatEnabled } from '../../services/aiChat';
import { aiChatOpenState } from '../aichat/aiChatState';
import AccountMenu from './AccountMenu';
import CommandPalette from './CommandPalette';
import { useFlatNavItems } from './navConfig';
import { mobileSidebarOpenState } from './sidebarState';

const MOBILE_BREAKPOINT = '(max-width:1023.95px)';

function useCurrentCrumb() {
    const location = useLocation();
    const navItems = useFlatNavItems();

    const exact = navItems.find((item) => item.path === location.pathname);
    if (exact) return { section: exact.sectionLabel, page: exact.label };

    // Fallback for detail/edit routes (e.g. /sales/:id, /customers/view/:id):
    // match the longest registered nav path that prefixes the current route.
    const prefixMatch = navItems
        .filter((item) => item.path !== '/' && location.pathname.startsWith(item.path))
        .sort((a, b) => b.path.length - a.path.length)[0];
    if (prefixMatch) return { section: prefixMatch.sectionLabel, page: prefixMatch.label };

    return null;
}

/**
 * App shell topbar — DESIGN-SYSTEM.md §3.2.
 * Fixed height 68px, sticky, backdrop blur, breadcrumb + search trigger +
 * theme toggle + user chip. No hamburger/breadcrumb search on mobile widths
 * other than the hamburger itself (breadcrumb hides below 760px per §2.7).
 */
export default function AppBarCustom() {
    const theme = useTheme();
    const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
    const [, setMobileOpen] = useRecoilState(mobileSidebarOpenState);
    const setAiChatOpen = useSetRecoilState(aiChatOpenState);
    const [paletteOpen, setPaletteOpen] = useState(false);
    const crumb = useCurrentCrumb();
    const location = useLocation();
    const { hasPermission } = usePermissions();

    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setPaletteOpen(true);
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <Box
            component="header"
            sx={{
                height: 68,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                px: { xs: 1.5, sm: 3 },
                minWidth: 0,
                backgroundColor: 'background.paper',
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'sticky',
                top: 0,
                zIndex: theme.zIndex.appBar,
                backdropFilter: 'blur(8px)',
            }}
        >
            {isMobile && (
                <Box
                    component="button"
                    onClick={() => setMobileOpen(true)}
                    aria-label="Abrir menu"
                    sx={{
                        width: 38, height: 38, borderRadius: '10px', border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'background.paper', color: 'text.secondary', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                    }}
                >
                    <MenuIcon size={18} />
                </Box>
            )}

            {!isMobile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 13, color: 'text.disabled', whiteSpace: 'nowrap' }}>
                    <Typography component="b" sx={{ color: 'text.primary', fontWeight: 600, fontSize: 13 }}>FlowVendas</Typography>
                    {crumb && (
                        <>
                            <ChevronRight size={14} style={{ opacity: 0.5 }} />
                            <span>{crumb.section}</span>
                            <ChevronRight size={14} style={{ opacity: 0.5 }} />
                            <Typography component="b" sx={{ color: 'text.primary', fontWeight: 600, fontSize: 13 }}>{crumb.page}</Typography>
                        </>
                    )}
                </Box>
            )}

            <Box
                onClick={() => setPaletteOpen(true)}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 420,
                    mx: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    px: { xs: 1.25, sm: 1.75 },
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    height: 40,
                    borderRadius: '10px',
                    backgroundColor: 'background.subtle',
                    border: `1px solid ${theme.palette.divider}`,
                    color: 'text.disabled',
                    cursor: 'pointer',
                    fontSize: '13.5px',
                    '&:hover': { borderColor: 'primary.main' },
                }}
            >
                <Search size={16} style={{ flexShrink: 0 }} />
                <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>Buscar no sistema…</Box>
                <Box component="kbd" sx={{ display: { xs: 'none', md: 'block' }, ml: 'auto', fontSize: 11, backgroundColor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: '6px', px: '6px', py: '2px' }}>
                    ⌘K
                </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1.25 }, ml: 'auto', flexShrink: 0 }}>
                {/* Na página /chat o painel lateral some (a conversa já está em tela cheia). */}
                {isAiChatEnabled() && hasPermission('ai.use') && location.pathname !== '/chat' && (
                    <Box
                        component="button"
                        onClick={() => setAiChatOpen(true)}
                        aria-label="Abrir Assistente de IA"
                        title="Assistente de IA"
                        sx={{
                            width: 38, height: 38, borderRadius: '10px', border: `1px solid ${theme.palette.divider}`,
                            backgroundColor: 'background.paper', color: 'text.secondary', display: 'flex',
                            alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
                            '&:hover': { borderColor: 'primary.main', color: 'text.primary' },
                        }}
                    >
                        <Sparkles size={17} />
                    </Box>
                )}
                <AccountMenu />
            </Box>

            <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
        </Box>
    );
}
